import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

interface DeletePositDialogProps {
  positId: string;
  positTitle: string;
  onDeleted?: () => void;
  triggerClassName?: string;
  triggerSize?: "sm" | "default" | "lg";
  showLabel?: boolean;
  variant?: "destructive" | "outline";
}

const DeletePositDialog: React.FC<DeletePositDialogProps> = ({
  positId,
  positTitle,
  onDeleted,
  triggerClassName = "",
  triggerSize,
  showLabel = true,
  variant = "destructive",
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      // Use secure edge function for complete deletion (includes vector cleanup)
      const { deletePositSecurely } = await import('../../utils/secureApiCalls');
      
      const result = await deletePositSecurely(positId);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      toast({
        title: "Posit Deleted",
        description: "Your posit has been deleted successfully "
      });

      // Refresh the posits list
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-posits", user.id] });
      }

      // Call the onDeleted callback if provided
      if (onDeleted) {
        onDeleted();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete posit",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Manual deletion fallback
  const handleManualDelete = async () => {
    try {
      // Delete in reverse dependency order to avoid foreign key violations
      const deletions = [
        // Delete analytics and usage data first
        supabase.from('posit_analytics').delete().eq('posit_id', positId),
        supabase.from('posit_embed_analytics').delete().eq('posit_id', positId),
        supabase.from('posit_preview_usage').delete().eq('posit_id', positId),
        supabase.from('posit_reviews').delete().eq('posit_id', positId),
        
        // Delete chat-related data
        supabase.from('chat_messages').delete().eq('posit_id', positId),
        supabase.from('anonymous_chat_sessions').delete().eq('posit_id', positId),
        supabase.from('chat_history').delete().eq('posit_id', positId),
        supabase.from('chat_sessions').delete().eq('posit_id', positId),
        
        // Delete task-related data
        supabase.from('posit_task_flags').delete().eq('posit_id', positId),
        supabase.from('posit_tasks').delete().eq('posit_id', positId),
        
        // Delete subscriptions and shares
        supabase.from('posit_subscriptions').delete().eq('posit_id', positId),
        supabase.from('posit_shares').delete().eq('posit_id', positId),
        
        // Delete attachments (DB rows); storage cleanup handled below
        supabase.from('posit_attachments').delete().eq('posit_id', positId),
        
        // Clear any user references to this posit
        supabase.from('users').update({ posit_id: null }).eq('posit_id', positId),
      ];

      // Execute all deletions in parallel
      const results = await Promise.allSettled(deletions);
      
      // Check for any failed deletions
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
      }

      // Cleanup storage objects in the private bucket for this posit
      try {
        const prefix = user?.id ? `${user.id}/${positId}` : `${positId}`;
        const { data: files, error: listError } = await supabase
          .storage
          .from('posit-attachments')
          .list(prefix, { limit: 1000 });
        if (!listError && Array.isArray(files) && files.length > 0) {
          const paths = files.map(f => `${prefix}/${f.name}`);
          const { error: removeError } = await supabase
            .storage
            .from('posit-attachments')
            .remove(paths);
          if (removeError) {
          }
        }
      } catch (e) {
      }

      // Finally, delete the posit itself
      const { error: positError } = await supabase
        .from('posits')
        .delete()
        .eq('id', positId);

      if (positError) throw positError;

      toast({
        title: "Posit Deleted",
        description: "Your posit has been deleted successfully"
      });

      // Refresh the posits list
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-posits", user.id] });
      }

      // Call the onDeleted callback if provided
      if (onDeleted) {
        onDeleted();
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size={triggerSize ?? "sm"}
          className={variant === "destructive" ? `text-destructive hover:text-destructive ${triggerClassName}` : triggerClassName}
        >
          <Trash2 className="w-3 h-3" />
          {showLabel && <span className="ml-1">Delete</span>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Posit
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            Are you sure you want to delete <span className="font-semibold">"{positTitle}"</span>?
          </AlertDialogDescription>
          <div className="text-sm text-muted-foreground text-left">
            <p className="mb-2">This action cannot be undone. This will permanently delete:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>The posit and all its content</li>
              <li>All chat history and messages</li>
              <li>Analytics and usage data</li>
              <li>Subscriber data and reviews</li>
              <li>Any shared access permissions</li>
            </ul>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : "Delete Forever"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeletePositDialog;